import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { executeSearchRequest } from 'tio-data/requests/search';
import {
  EMPLOYEE_MATCHES,
  EXACT_NAME_AGG,
  buildPeopleSearchBody,
  hasNameTokens,
} from 'tio-employee/utils/people-search';
import { PARTNER_VISIBLE_ROLE_KEYS } from 'tio-employee/utils/people-facets';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import type { PeopleSearchParams } from 'tio-employee/utils/people-search';
import type { TermsBucket } from 'tio-common/utils/open-search-response-builder';
import type Transition from '@ember/routing/transition';
import type { Store } from '@warp-drive/core';
import type PartnerService from 'tio-employee/services/partner';

type Params = PeopleSearchParams;

/** One person card, projected from the partner company's matched employment. */
export type EmployeeRow = {
  id: string;
  payrollId: string;
  firstName: string;
  lastName: string;
  email: string;
  participationStatus: string;
  createdAt?: string;
  jurisdictions: string[];
  isActiveEmployee: boolean;
  isEnrolled: boolean;
  isRegistered: boolean;
  matchedQueries?: string[];
};

/** Bucket counts backing the filter rail, one entry per facet group. */
export type PeopleFacets = {
  status: Record<string, number>;
  enrollment: Record<string, number>;
  participation: Record<string, number>;
  roles: TermsBucket[];
};

export type EmployeeIndexModel = {
  items: EmployeeRow[];
  total: number;
  isLoading: boolean;
  facets?: PeopleFacets;
  /**
   * Exact name matches across the WHOLE result set, or undefined when the
   * search carried no name tokens. Zero with results means every match was
   * fuzzy — page one can't establish that on its own.
   */
  exactNameMatches?: number;
};

/**
 * One matched employment from `inner_hits`. The query is sent to OpenSearch raw
 * in snake_case, but the API serializes the response to camelCase (Accept:
 * application/vnd.api+json), so we read camelCase and keep snake_case as a
 * defensive fallback.
 */
type EmployeeMatch = {
  employeeIdKeyword?: string | number;
  firstNameText?: string;
  lastNameText?: string;
  payrollIdKeyword?: string;
  emailText?: string;
  participationStatusKeyword?: string;
  createdAtDate?: string;
  jurisdictionsKeyword?: string[];
  employeeIsActiveBoolean?: boolean;
  employeeIsEnrolledBoolean?: boolean;
  employeeIsRegisteredBoolean?: boolean;
  employee_id_keyword?: string | number;
  first_name_text?: string;
  last_name_text?: string;
  payroll_id_keyword?: string;
  email_text?: string;
  participation_status_keyword?: string;
  created_at_date?: string;
  jurisdictions_keyword?: string[];
  employee_is_active_boolean?: boolean;
  employee_is_enrolled_boolean?: boolean;
  employee_is_registered_boolean?: boolean;
};

type InnerHit = {
  _source?: EmployeeMatch;
  matchedQueries?: string[];
  matched_queries?: string[];
};

type PersonHit = {
  innerHits?: Record<string, { hits?: { hits?: InnerHit[] } }>;
  inner_hits?: Record<string, { hits?: { hits?: InnerHit[] } }>;
  matchedQueries?: string[];
  matched_queries?: string[];
};

export default class AuthenticatedAdminEmployeesRoute extends Route {
  @service declare store: Store;
  @service declare partner: PartnerService;

  queryParams = {
    search: { refreshModel: true, replace: true },
    participationStatus: { refreshModel: true, replace: true },
    status: { refreshModel: true, replace: true },
    enrollment: { refreshModel: true, replace: true },
    jurisdictions: { refreshModel: true, replace: true },
    page: { refreshModel: true, replace: true },
    limit: { refreshModel: true, replace: true },
  };

  async model(params: Params): Promise<EmployeeIndexModel> {
    const companyId = this.partner.company?.id;

    // SearchPolicy requires the company scope for company-admin/employee roles.
    // Without a company we have nothing to scope to — return an empty result.
    if (!companyId) {
      return { items: [], total: 0, isLoading: false };
    }

    const query = {
      'model-type': 'Person',
      'search-body': buildPeopleSearchBody(companyId, params),
    };

    const { content: response } = (await this.store.request(executeSearchRequest(query))) as {
      content: OpenSearchResponse;
    };

    const rb = OpenSearchResponseBuilder.from(response);

    const items = (response.hits?.hits ?? [])
      .map((hit) => this.rowFromPersonHit(hit as PersonHit))
      .filter((row): row is EmployeeRow => row !== undefined);

    return {
      items,
      total: rb.getTotalCount(),
      isLoading: false,
      facets: {
        status: rb.getNestedFilteredNamedFiltersAggregation('facetStatus'),
        enrollment: rb.getNestedFilteredNamedFiltersAggregation('facetEnrollment'),
        participation: rb.getNestedFilteredNamedFiltersAggregation('facetParticipation'),
        // The query already bucketed the allowlist; this is defence in depth.
        roles: rb
          .getNestedFilteredTermsAggregation('facetRoles')
          .filter((bucket) => PARTNER_VISIBLE_ROLE_KEYS.includes(bucket.key)),
      },
      ...(hasNameTokens(params.search) && {
        exactNameMatches: rb.getNestedFilteredNamedFiltersAggregation(EXACT_NAME_AGG).exact ?? 0,
      }),
    };
  }

  /**
   * Project a Person hit to a card from its matched employment.
   *
   * Reads `inner_hits` rather than the parent `employees_nested` array: the
   * query returns only this company's employment there, so there is no other
   * employer's record present to filter out — or to have shipped.
   */
  private rowFromPersonHit(hit: PersonHit): EmployeeRow | undefined {
    const inner = hit.innerHits ?? hit.inner_hits ?? {};
    const innerHit = inner[EMPLOYEE_MATCHES]?.hits?.hits?.[0];
    const match = innerHit?._source;
    if (!match) return undefined;

    // Named clauses live inside the nested query, so OpenSearch reports what
    // matched on the inner hit; the parent only carries root-level clause names.
    const matchedQueries =
      innerHit?.matchedQueries ??
      innerHit?.matched_queries ??
      hit.matchedQueries ??
      hit.matched_queries;
    const jurisdictions = match.jurisdictionsKeyword ?? match.jurisdictions_keyword ?? [];

    return {
      id: String(match.employeeIdKeyword ?? match.employee_id_keyword ?? ''),
      payrollId: match.payrollIdKeyword ?? match.payroll_id_keyword ?? '',
      firstName: match.firstNameText ?? match.first_name_text ?? '',
      lastName: match.lastNameText ?? match.last_name_text ?? '',
      email: match.emailText ?? match.email_text ?? '',
      participationStatus:
        match.participationStatusKeyword ?? match.participation_status_keyword ?? '',
      createdAt: match.createdAtDate ?? match.created_at_date,
      jurisdictions: jurisdictions.filter((key) => PARTNER_VISIBLE_ROLE_KEYS.includes(key)),
      isActiveEmployee: Boolean(match.employeeIsActiveBoolean ?? match.employee_is_active_boolean),
      isEnrolled: Boolean(match.employeeIsEnrolledBoolean ?? match.employee_is_enrolled_boolean),
      isRegistered: Boolean(
        match.employeeIsRegisteredBoolean ?? match.employee_is_registered_boolean
      ),
      ...(matchedQueries && { matchedQueries }),
    };
  }

  loading = (transition: Transition, _originRoute: unknown) => {
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const controller = this.controllerFor('authenticated.admin.employees.index');
    controller.set('loading', true);
    transition.promise?.finally(function () {
      controller.set('loading', false);
    });
    return true;
  };
}
